import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getCurrentUser } from "../services/pocketbase";

const DeveloperConsole = () => {
  const [open, setOpen] = useState(false);
  const [command, setCommand] = useState("");
  const [output, setOutput] = useState([
    { type: "system", content: "Developer Console v1.0.0" },
    { type: "system", content: "Type 'help' for available commands" },
  ]);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const user = getCurrentUser();
  const outputEndRef = useRef(null);

  // Auto-scroll to bottom when output changes
  useEffect(() => {
    if (outputEndRef.current) {
      outputEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [output]);

  useEffect(() => {
    // Listen for the secret phrases
    let secretBuffer = "";
    let timer = null;

    const handleKeyDown = (e) => {
      // Add the key to the buffer
      secretBuffer += e.key.toLowerCase();

      // Reset the buffer after 2 seconds of inactivity
      clearTimeout(timer);
      timer = setTimeout(() => {
        secretBuffer = "";
      }, 2000);

      // Check if the buffer contains any of our secret phrases
      if (
        secretBuffer.includes("devmode") ||
        secretBuffer.includes("open sesame")
      ) {
        setOpen(true);
        secretBuffer = "";
        clearTimeout(timer);

        // Only attempt to assign role if user has Discord ID and role hasn't been assigned yet
        if (user?.discord_id && !roleAssigned) {
          assignDiscordRole();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timer);
    };
  }, [user, roleAssigned]);

  // Function to assign Discord role
  const assignDiscordRole = async () => {
    try {
      setRoleError(null);

      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1375132557078827109", // Use an appropriate role ID for your server
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to assign role");
      }

      setRoleAssigned(true);
      addOutput("system", "Developer role assigned to your Discord account!");
    } catch (err) {
      console.error("Error assigning Developer role:", err);
      setRoleError(err.message || "Failed to assign Discord role");
      addOutput("error", `Role assignment failed: ${err.message}`);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCommandSubmit = (e) => {
    e.preventDefault();
    if (!command.trim()) return;

    // Add the command to the output
    addOutput("user", command);

    // Process the command
    processCommand(command);

    // Clear the command input
    setCommand("");
  };

  const addOutput = (type, content) => {
    setOutput((prev) => [...prev, { type, content }]);
  };

  const processCommand = (cmd) => {
    const lowerCmd = cmd.toLowerCase().trim();

    // Basic command processing
    switch (lowerCmd) {
      case "help":
        addOutput("system", "Available commands:");
        addOutput("system", "- help: Show this help message");
        addOutput("system", "- clear: Clear the console");
        addOutput("system", "- whoami: Show current user info");
        addOutput("system", "- version: Show application version");
        addOutput("system", "- exit: Close the console");
        addOutput("system", "- joke: Tell a developer joke");
        break;

      case "clear":
        setOutput([
          { type: "system", content: "Developer Console v1.0.0" },
          { type: "system", content: "Type 'help' for available commands" },
        ]);
        break;

      case "whoami":
        if (user) {
          addOutput("system", `Logged in as: ${user.username}`);
          addOutput("system", `Email: ${user.email}`);
          addOutput("system", `ID: ${user.discord_id}`);
          addOutput(
            "system",
            `Discord linked: ${user.discord_id ? "Yes" : "No"}`
          );
        } else {
          addOutput("system", "Not logged in");
        }
        break;

      case "version":
        addOutput("system", "Website version: 1.0.0");
        addOutput("system", "Edit date: " + new Date().toLocaleDateString());
        break;

      case "exit":
        handleClose();
        break;

      case "joke":
        const jokes = [
          "Why do programmers prefer dark mode? Because light attracts bugs!",
          "A SQL query walks into a bar, walks up to two tables and asks, 'Can I join you?'",
          "Why do Java developers wear glasses? Because they don't C#!",
          "How many programmers does it take to change a light bulb? None, that's a hardware problem.",
          "Why was the JavaScript developer sad? Because he didn't know how to 'null' his feelings.",
          "What's a programmer's favorite place? The Foo Bar.",
        ];
        const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];
        addOutput("system", randomJoke);
        break;

      default:
        addOutput("error", `Unknown command: ${cmd}`);
        addOutput("system", "Type 'help' for available commands");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1e1e1e",
          color: "#00ff00",
          fontFamily: "monospace",
          border: "1px solid #00ff00",
          boxShadow: "0 0 10px rgba(0, 255, 0, 0.5)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #00ff00",
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontFamily: "monospace" }}
        >
          Developer Console
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            height: "400px",
            overflowY: "auto",
            p: 2,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#1e1e1e",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#00ff00",
              borderRadius: "4px",
            },
          }}
        >
          {output.map((line, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              {line.type === "user" ? (
                <Typography sx={{ color: "#ffffff" }}>
                  <span style={{ color: "#00ff00" }}>{">"}</span> {line.content}
                </Typography>
              ) : line.type === "error" ? (
                <Typography sx={{ color: "#ff0000" }}>
                  {line.content}
                </Typography>
              ) : (
                <Typography sx={{ color: "#00ff00" }}>
                  {line.content}
                </Typography>
              )}
            </Box>
          ))}
          {/* This empty div is used as a reference for auto-scrolling */}
          <div ref={outputEndRef} />
        </Box>
        <Divider sx={{ borderColor: "#00ff00" }} />
        <Box
          component="form"
          onSubmit={handleCommandSubmit}
          sx={{
            display: "flex",
            p: 2,
            borderTop: "1px solid #00ff00",
          }}
        >
          <Typography sx={{ color: "#00ff00", mr: 1 }}>{">"}</Typography>
          <TextField
            fullWidth
            variant="standard"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            autoFocus
            InputProps={{
              disableUnderline: true,
              style: {
                color: "#ffffff",
                fontFamily: "monospace",
              },
            }}
            sx={{ "& .MuiInputBase-input": { p: 0 } }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DeveloperConsole;
